// /shopping — pharmacy shopping list. Add things the user wants to
// pick up next time at the pharmacy: refills, vitamins, OTC meds,
// wound care, pill organizers, etc. Categorized for easy scanning.

import { useEffect, useState } from 'react';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

type Category = 'rx' | 'otc' | 'vitamins' | 'first_aid' | 'pillbox' | 'other';

interface Item {
  id: string;
  name: string;
  category: Category;
  notes: string | null;
  bought: boolean;
  createdAt: string;
}

const CATEGORY_LABEL: Record<Category, string> = {
  rx: 'Prescription pickup',
  otc: 'Over the counter',
  vitamins: 'Vitamins and supplements',
  first_aid: 'First aid and wound care',
  pillbox: 'Pillbox and organizers',
  other: 'Other',
};

const CATEGORY_ICON: Record<Category, string> = {
  rx: '💊',
  otc: '🩹',
  vitamins: '🧪',
  first_aid: '🩺',
  pillbox: '📦',
  other: '🛒',
};

const SUGGESTIONS: Array<{ name: string; category: Category }> = [
  { name: 'Pill organizer (weekly, 4 times a day)', category: 'pillbox' },
  { name: 'Band-aids, assorted', category: 'first_aid' },
  { name: 'Antibacterial ointment', category: 'first_aid' },
  { name: 'Vitamin D3 1000 IU', category: 'vitamins' },
  { name: 'Multivitamin for adults 50+', category: 'vitamins' },
  { name: 'Calcium + magnesium', category: 'vitamins' },
  { name: 'Tylenol or acetaminophen 500 mg', category: 'otc' },
  { name: 'Allergy tablets', category: 'otc' },
  { name: 'Reading glasses', category: 'other' },
  { name: 'Petroleum jelly (Vaseline)', category: 'first_aid' },
];

export function Shopping(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<Category>('vitamins');

  const reload = async () => {
    const res = await authedFetch('/api/shopping');
    const json = (await res.json()) as { ok: boolean; data?: { items: Item[] } };
    if (json.ok && json.data) setItems(json.data.items);
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    void reload();
  }, []);

  const add = async (n: string, c: Category) => {
    if (!n.trim() || busy) return;
    setBusy(true);
    try {
      await authedFetch('/api/shopping', {
        method: 'POST',
        body: JSON.stringify({ name: n.trim(), category: c }),
      });
      setName('');
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item: Item) => {
    setBusy(true);
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, bought: !i.bought } : i)));
    try {
      await authedFetch(`/api/shopping/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ bought: !item.bought }),
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item: Item) => {
    setBusy(true);
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try {
      await authedFetch(`/api/shopping/${item.id}`, { method: 'DELETE' });
    } finally {
      setBusy(false);
    }
  };

  const grouped = (Object.keys(CATEGORY_LABEL) as Category[]).map((c) => ({
    category: c,
    items: items.filter((i) => i.category === c),
  }));

  const unboughtCount = items.filter((i) => !i.bought).length;

  return (
    <AppPage title="Pharmacy list" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Don't forget</p>
          <p className="food-intro__text">
            Add things you want to pick up next time you're at the pharmacy. Tap an item to mark it
            bought.{' '}
            {unboughtCount > 0
              ? `${unboughtCount} item${unboughtCount === 1 ? '' : 's'} to buy.`
              : 'Your list is clear.'}
          </p>
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Add to list</h3>
        <div
          style={{
            display: 'grid',
            // Stack input over select on narrow viewports so neither
            // forces horizontal scroll on iPhone-class devices.
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 8,
          }}
        >
          <input
            type="text"
            aria-label="Item name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What do you need?"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add(name, category);
            }}
            style={{
              padding: '12px 14px',
              fontSize: 18,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              minWidth: 0,
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          <select
            aria-label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            style={{
              padding: '12px 14px',
              fontSize: 16,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-primary)',
              minWidth: 0,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {(Object.keys(CATEGORY_LABEL) as Category[]).map((c) => (
              <option key={c} value={c}>
                {CATEGORY_ICON[c]} {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginTop: 12 }}>
          <SeniorButton
            variant="primary"
            onClick={() => void add(name, category)}
            disabled={busy || !name.trim()}
            fullWidth
          >
            Add to list
          </SeniorButton>
        </div>

        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '16px 0 8px' }}>
          Or tap a common item:
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SUGGESTIONS.map((s) => (
            <button
              key={s.name}
              type="button"
              className="followup-chip"
              onClick={() => void add(s.name, s.category)}
              disabled={busy}
            >
              {CATEGORY_ICON[s.category]} {s.name}
            </button>
          ))}
        </div>
      </section>

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}

      {!loading && items.length === 0 ? (
        <EmptyIllustration
          kind="shopping"
          title="Nothing on the list"
          body="Add things you want to remember to pick up. We'll keep them organized by category."
        />
      ) : null}

      {grouped.map((g) =>
        g.items.length === 0 ? null : (
          <section key={g.category} className="account-section">
            <h3 className="account-section__title">
              <span aria-hidden="true" style={{ marginRight: 8 }}>
                {CATEGORY_ICON[g.category]}
              </span>
              {CATEGORY_LABEL[g.category]}
            </h3>
            <ul className="shopping-list">
              {g.items.map((it) => (
                <li key={it.id} className={`shopping-item${it.bought ? ' is-bought' : ''}`}>
                  <button
                    type="button"
                    className={`adh-check${it.bought ? ' is-taken' : ''}`}
                    onClick={() => void toggle(it)}
                    aria-pressed={it.bought}
                    aria-label={it.bought ? 'Mark as not bought' : 'Mark as bought'}
                  >
                    {it.bought ? (
                      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                        <title>Bought</title>
                        <path
                          d="M5 12l4 4 10-10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </button>
                  <span className="shopping-item__name">{it.name}</span>
                  <button
                    type="button"
                    onClick={() => void remove(it)}
                    aria-label="Remove from list"
                    style={{
                      background: 'transparent',
                      border: 0,
                      color: 'var(--color-text-tertiary)',
                      cursor: 'pointer',
                      padding: 8,
                    }}
                  >
                    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                      <title>Remove</title>
                      <path
                        d="M5 5l10 10M15 5L5 15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ),
      )}
    </AppPage>
  );
}
